import { useEffect, useRef, useState } from 'preact/hooks';
import { pickLocale, type CheckoutConfigDto } from './api.js';
import { toneStyleFor } from './MethodTiles.js';

type MethodTile = CheckoutConfigDto['strategy']['methodEligibility'][number];
type Strategy = CheckoutConfigDto['strategy'];

/**
 * Contextual "what happens after you pay" band — opt-in by the operator
 * (CheckoutStrategy.contextualProcessing.enabled). Renders BELOW the method
 * tiles and ABOVE the Pay button. Content swaps with a 150ms crossfade when
 * the shopper picks a different method.
 *
 * Layout is two stacked rows inside a rounded card:
 *   1. Heading (static eyebrow + clock icon)
 *   2. Selected-method pill + detail paragraph, OR italic empty state.
 *
 * Reserves a min-height of 8.5rem so the page doesn't shift when the
 * shopper toggles between methods of different copy length.
 */
export function ContextualProcessingBand(props: {
  config: NonNullable<Strategy['contextualProcessing']>;
  methods: MethodTile[];
  selected: string | null;
  locale: string;
}) {
  const { config, methods, selected, locale } = props;
  const method = selected ? methods.find((m) => m.paymentMethod === selected) ?? null : null;

  // Default copy + locale-aware overrides
  const heading = pickLocale(config.heading, locale) || 'What happens after you pay';
  const emptyState =
    pickLocale(config.emptyStateText, locale) ||
    'Select a payment method to see processing details.';

  // Crossfade: hold previous content + opacity-tween out, swap in the new.
  const [renderKey, setRenderKey] = useState(0);
  const lastSelected = useRef<string | null>(selected);
  useEffect(() => {
    if (lastSelected.current !== selected) {
      lastSelected.current = selected;
      setRenderKey((k) => k + 1);
    }
  }, [selected]);

  const tone = toneStyleFor(method?.processingCallout?.tone);
  const detail = method
    ? pickLocale(method.processingDetail, locale) ||
      pickLocale(method.processingCallout?.text, locale) ||
      ''
    : '';
  const methodLabel = method
    ? pickLocale(method.label, locale) ||
      method.paymentMethod
        .split(/[_-]/)
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(' ')
    : '';
  const methodIcon =
    method?.processingCallout?.icon || (method ? tone.icon : 'schedule');

  return (
    <section
      aria-label={heading}
      class="rounded-2xl bg-surface-container-low p-5 sm:p-6 shadow-sm border border-outline-variant/30"
      style={{ minHeight: '8.5rem' }}
    >
      <p class="upg-eyebrow text-on-surface-variant flex items-center gap-1.5">
        <span class="material-symbols-outlined" style={{ fontSize: '14px' }} aria-hidden="true">
          schedule
        </span>
        <span>{heading}</span>
      </p>

      <div
        key={renderKey}
        class="mt-3 transition-opacity duration-200 ease-out opacity-100 animate-fade-in"
        style={{ animation: 'upgFade 200ms ease-out' }}
      >
        {method ? (
          <div class="space-y-2">
            <span
              class={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${tone.pill}`}
            >
              <span class="material-symbols-outlined" style={{ fontSize: '14px' }} aria-hidden="true">
                {methodIcon}
              </span>
              {methodLabel}
            </span>
            {detail && (
              <p class="text-body-sm text-on-surface leading-relaxed max-w-prose">{detail}</p>
            )}
          </div>
        ) : (
          <p class="text-body-sm italic text-on-surface-variant max-w-prose">{emptyState}</p>
        )}
      </div>

      <style>
        {`@keyframes upgFade { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: translateY(0); } }`}
      </style>
    </section>
  );
}
